/**
 * Job status — the one label map and the one style map.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * There were four copies of this decision, and three of them were wrong. The
 * office side (pages/jobs/JobDetail.tsx) carried a correct exhaustive map; the
 * three field screens each hand-rolled a 3-arm ternary over `active`,
 * `scheduled` and `completed` and rendered the raw enum value for anything
 * else. So a job on hold displayed to a field worker as the literal string
 * `on_hold` in the grey "unknown" pill — and `pending` and `cancelled` did the
 * same. FieldHomeScreen's copy did not even have the `completed` arm.
 *
 * That was survivable while nothing in the field app could produce `on_hold`.
 * The crew start/hold endpoints (API #316) make it a status a field worker
 * reaches by pressing a button on their own phone, so it stopped being
 * theoretical.
 *
 * Per CLAUDE.md: labels come from a shared exhaustive map keyed on every enum
 * value, never a binary check and never a ternary that falls through to the
 * raw value.
 *
 * KEYED ON THE FULL ENUM. All six values of the `job_status` Postgres enum are
 * present, so there is no "default" arm doing real work. `Record<JobStatus, …>`
 * rather than `Record<string, …>` is the point: adding a seventh status to the
 * union fails the typecheck here instead of silently rendering as grey.
 */

/** Every value of the API's `job_status` enum. */
export type JobStatus =
  | 'pending'
  | 'scheduled'
  | 'active'
  | 'on_hold'
  | 'completed'
  | 'cancelled';


/**
 * The 20% wash, for the one surface that still wants a container rather than a
 * signal: the dashboard job-board CARD.
 *
 * IT IS NO LONGER THE BACKGROUND HALF OF `JOB_STATUS_STYLES` — that map went
 * solid, and this did not follow it. The board card is not the status, it is a
 * container for jobs of that status, and six 320px cards at full saturation is
 * a wall of colour. The card holds the group; the solid pill inside it carries
 * the signal. Everywhere else that used to render this tint now renders
 * `JOB_STATUS_SOLID`.
 *
 * NOTHING READS TEXT DIRECTLY OFF THIS ANY MORE. The card's own secondary text
 * is `text-foreground` / `text-muted-foreground` and measures 8.87-13.01:1
 * against these washes; the status label that used to sit here as bare text is
 * a solid pill now. The light-mode remaps that made a `text-*-400` half legible
 * on this tint (`src/styles/theme.css`, emerald 5.48 / orange 5.18 / gray 10.31)
 * are still there and still correct — they are simply no longer what any
 * job-status surface depends on.
 *
 * THE 3:1 OBJECT BAR DOES NOT APPLY TO THIS ANY MORE EITHER, and that is the
 * point of keeping it. Measured, this wash sits at 1.10-1.44:1 against the
 * surface behind it — as a PILL that was a failure, which is what sent the whole
 * palette solid. As a card tint under a pill that carries the signal at
 * 4.35-10.56, it is background, and background is allowed to be quiet.
 *
 * `completed` IS `bg-purple-500/20`, NOT EMERALD. The tint has to follow the
 * fill or the dashboard board card contradicts the pill sitting on it — an
 * emerald-washed card under a purple pill, which is what changing only
 * `--status-completed` would have shipped. See theme.css:25 for why the hue
 * moved. There are SIX places `completed`'s hue is encoded and they move
 * together: the two theme vars, the `cal-pill-trailing` fallback, the
 * `tailwind.config.js` fallback, this map, and `BOARD_EDGE` in
 * components/dashboard/JobStatusBoard.tsx.
 *
 * IT WAS SEVEN UNTIL THE JOBS RE-SKIN. The seventh was a bare text colour in
 * screens/JobsScreen.tsx, rendered on that screen's narrow-viewport card only.
 * That card now renders the canonical pill through `jobStatusStyle`, so the
 * hue reaches it from this file and there is no seventh copy to keep in step.
 *
 * THE `completed` ENTRY IS UNREACHABLE IN THE RUNNING APP TODAY, and it was
 * changed anyway. `jobStatusTint` has exactly one consumer —
 * components/dashboard/JobStatusBoard.tsx — whose only caller,
 * screens/HomeScreen.tsx:842, filters `completed` and `cancelled` OUT of the
 * jobs it passes. So no completed card renders on that board, before or after,
 * and the dashboard screenshots for this change are byte-identical.
 *
 * It still moves, for two reasons worth stating so nobody "reverts the dead
 * one": `JobStatusBoard` is a general component that handles all six statuses
 * and any future caller may pass one, and a map where five entries agree with
 * the palette and the sixth silently does not is precisely the drift the seven-
 * encoding list above exists to prevent. job-status.stripe.test.ts pins it.
 *
 * SLASH SYNTAX IS LOAD-BEARING. `bg-emerald-500/20` and
 * `bg-emerald-500 bg-opacity-20` paint the same pixels but are not
 * interchangeable: theme.css:168-175 matches the bare `.bg-amber-500` class and
 * forces `color:#ffffff !important` on the assumption that a solid colour class
 * means a solid chip. Composing the solid class with a separate opacity utility
 * left the pill translucent while the white-text rule still fired — white on a
 * 20% amber wash, 1.17:1. The slash form never matches that selector, and it is
 * what theme.css:178-185 already normalises for light mode.
 */
export const JOB_STATUS_TINT: Record<JobStatus, string> = {
  pending: 'bg-blue-500/20',
  scheduled: 'bg-amber-500/20',
  active: 'bg-green-500/20',
  on_hold: 'bg-orange-500/20',
  completed: 'bg-purple-500/20',
  cancelled: 'bg-gray-500/20',
};

/**
 * The six hues as a SOLID fill — the pills, and the dots that are left.
 *
 * This used to be `JOB_STATUS_DOT` and it was only ever a dot. The dispatch
 * calendar pill, the dashboard board pill and the four field/office badges all
 * paint one of these now, so a name that says "dot" at a pill call site is how
 * somebody adds a second map. One name, one map, nine call sites.
 *
 * THESE REMAP PER THEME. Dark is Tailwind -400, light is -700, defined at
 * src/styles/theme.css:25-30 and 122-127. That split was measured for the dots
 * in PR #395 and it is what makes the solid PILL possible too, for a reason
 * worth stating because it is counter-intuitive:
 *
 *   A pill needs its label at 4.5:1 AND itself at 3:1 against the surface.
 *   White text needs a fill at luminance <= 0.183. 3:1 against the dark card
 *   (L = 0.022) needs a fill at luminance >= 0.165. Nothing in the Tailwind
 *   palette lands in that tenth-of-a-unit window, so in DARK MODE there is no
 *   shade at which white text on a solid pill works: measured across six hues
 *   and five backdrops, a static -700 fill with white text fails the object bar
 *   in 18 of 30 cells, worst 1.42.
 *
 * So the pill flips per theme rather than picking a shade: light gets a dark
 * fill with white text (the reference design, exactly), dark gets a bright fill
 * with near-black text (what `.bg-fieldiq-accent` has always done). Both live
 * in `--status-fg`. Worst cell across all six statuses x six surfaces x two
 * themes is 4.35; worst label is 5.02.
 *
 * WHAT THIS REPLACED, and why red no longer means "done": screens/JobsScreen.tsx
 * and components/dashboard/JobStatusBoard.tsx both pinned `completed` to
 * `bg-red-500`, because a static emerald measured 2.54:1 on a light card and
 * red managed 3.76. The per-theme split fixed that and both overrides went.
 *
 * `completed` IS NOW PURPLE, and that is a separability change rather than a
 * contrast one — emerald passed everywhere it was measured. It sat at dE2000
 * 7.28 from `active` green-400, the worst pair in the dark palette, and two
 * greens on a month grid read as one colour. Purple-400/-700 takes that pair to
 * 53.06 dark / 52.72 light and leaves no dark pair under 10. Full matrix in
 * job-status.test.ts and in theme.css:25.
 *
 * THE LAST OVERRIDE IS GONE TOO, as of the PR this paragraph used to ask for.
 * `STATUS_BADGE` in screens/JobsScreen.tsx pinned `completed` to red-400 text
 * on a 10% red-400 wash — a job-TYPE chip rather than a status badge, which is
 * why it outlived the two dot overrides above. It is purple now.
 *
 * THE CLASS STRING IS SPELLED OUT IN PROSE ABOVE RATHER THAN QUOTED VERBATIM,
 * which looks like fussiness and is not. Tailwind's content glob covers this
 * file and its scanner does not distinguish a comment from code, so quoting the
 * dead class here keeps emitting a real rule for it — the whole point of
 * removing it from the map. Verified by rule-level diff of the built CSS: with
 * the literal quoted, the background rule survives with zero call sites.
 *
 * IT WAS NOT ONLY AN INCOHERENCE. Measured, that chip's text was 4.38:1 on the
 * light card, the only failing cell in a map whose other five run 4.72-9.52.
 * The reason it read as cosmetic for three PRs is that it is MOBILE ONLY: the
 * chip renders inside `md:hidden`, so the "purple dot, purple label, red chip"
 * this paragraph described was the phone card, never the desktop table, whose
 * row has a dot and a `text-white` label and no chip.
 *
 * THEY ARE CONVERGED NOW, and the thing that unblocked it was not a new
 * argument. Both maps refused `JOB_STATUS_STYLES` on the ground that adopting
 * it would put a solid pill on a card that had none — a layout change nobody
 * had asked for. The jobs re-skin asked for one: that card was rebuilt to the
 * house register idiom, which carries a solid status pill by construction. So
 * the two local maps in screens/JobsScreen.tsx are gone, the card renders this
 * palette through `jobStatusStyle`, and the job-TYPE chip beside it is neutral
 * rather than tinted by a status the pill already names.
 *
 * WORTH KEEPING AS A SHAPE: a refusal resting on "that would require an
 * unrelated change" expires the day that change is made for its own reasons,
 * and nothing announces the expiry. Both docblocks had been restating the
 * refusal for three PRs by then.
 *
 * NO LINE NUMBER HERE ON PURPOSE. This paragraph used to say
 * "JobsScreen.tsx:55" and the map had drifted to :68 — a citation that ages
 * into a wrong one is worse than a grep, because it sends the next reader to a
 * real line that is not the one meant.
 *
 * THE CLASS NAMES ARE STILL LITERALS. `bg-status-on-hold` is spelled out, not
 * built from the status key — Tailwind's JIT only sees literal strings, and a
 * constructed class name emits no CSS with a missing colour as the only
 * symptom. `tailwind.config.js` additionally bakes each hue's -500 in as the
 * `var()` fallback, so an undefined variable degrades to what main rendered
 * rather than to a transparent pill, and job-status.test.ts asserts all six
 * variables exist in both theme blocks.
 */
export const JOB_STATUS_SOLID: Record<JobStatus, string> = {
  pending: 'bg-status-pending',
  scheduled: 'bg-status-scheduled',
  active: 'bg-status-active',
  on_hold: 'bg-status-on-hold',
  completed: 'bg-status-completed',
  cancelled: 'bg-status-cancelled',
};

/**
 * The pill: a solid fill and the text that rides on it.
 *
 * Built from `JOB_STATUS_SOLID` rather than spelled out again, because the two
 * cannot be allowed to drift — the fill and its paired text colour are one
 * decision. `text-status-fg` is the same class for every status; the fill is
 * what differs, and the text follows the THEME rather than the status.
 *
 * NEVER `text-white`. src/styles/theme.css:140 remaps `.text-white` in light
 * mode to `--fieldiq-heading` (#1C2430), which on these fills measures
 * 1.52-3.11:1. The token is not a stylistic preference over `text-white`; it is
 * the thing that stops that selector from firing.
 *
 * This map used to be `bg-<hue>-500/20 text-<hue>-400` — a tint welded to a
 * matching text colour. What replaced it is the same six hues at full strength.
 * The tint survives on its own as `JOB_STATUS_TINT` for the one surface that
 * still wants a wash.
 */
export const JOB_STATUS_STYLES: Record<JobStatus, string> = {
  pending: `${JOB_STATUS_SOLID.pending} text-status-fg`,
  scheduled: `${JOB_STATUS_SOLID.scheduled} text-status-fg`,
  active: `${JOB_STATUS_SOLID.active} text-status-fg`,
  on_hold: `${JOB_STATUS_SOLID.on_hold} text-status-fg`,
  completed: `${JOB_STATUS_SOLID.completed} text-status-fg`,
  cancelled: `${JOB_STATUS_SOLID.cancelled} text-status-fg`,
};

/**
 * The 4px LEFT STRIPE on a dispatch Kanban card.
 *
 * A THIRD MAP, NOT AN EXTENSION OF THE OTHER TWO, and that is enforced rather
 * than merely intended: job-status.test.ts asserts `JOB_STATUS_STYLES[s]` is
 * exactly `[JOB_STATUS_SOLID[s], 'text-status-fg']`, so appending a border class
 * to the pill map fails the suite. The pill and the stripe are different objects
 * on different surfaces — one carries text, the other never does.
 *
 * WHAT IT REPLACED: an 8px dot at the top-left of the card. With a stripe in the
 * same hue on the same edge the dot was punctuation repeating what the edge
 * already says — the identical argument that removed the dot from the calendar
 * pill and the dashboard board card in #413. The stripe does strictly more: it
 * also fixes a problem the dot never touched, below.
 *
 * THE CARD-VS-COLUMN PROBLEM, which is the real reason this is a stripe and not
 * a pill. A `JobCard` is `bg-fieldiq-dark` inside a `bg-fieldiq-card` column:
 * 1.21:1 in dark and 1.15:1 in LIGHT, where it reads as white-on-white and the
 * card has no visible edge at all. The stripe gives every card a 4.35-10.56
 * edge as a side effect of carrying the status. A pill inside the card would
 * have left the card itself invisible.
 *
 * NOT A TINTED CARD. Tinting the card body with the status hue was measured and
 * is worse on both axes: 1.16-1.29 dark and 1.40-1.47 light against the column —
 * so it does not fix the invisible-edge problem either — and it drops
 * `text-fieldiq-muted` on the card to 3.69-4.29, under the 4.5 that text needs,
 * across all twelve cells.
 *
 * MEASURED, every status x both themes, against the card body it sits on and
 * the column behind it: worst cell 4.35 (light `active` on the card body),
 * best 10.56. All twelve clear the 3:1 an object needs.
 *
 * THE CLASS NAMES ARE LITERALS, for the reason `JOB_STATUS_SOLID` spells its
 * own out: Tailwind's JIT only sees literal strings in source. This was
 * verified rather than assumed — building the real config against a probe file
 * emits `.border-l-status-completed { border-left-color: var(--status-completed,
 * #a855f7) }`, and a constructed `border-l-status-${key}` emits nothing at all,
 * with a missing stripe and no error as the only symptom. There is no
 * `safelist` in tailwind.config.js to fall back on.
 *
 * The width lives at the call site, not here: `border-l-4` is a layout decision
 * the card makes, and welding it in would stop the same hue being reused at a
 * different weight.
 */
export const JOB_STATUS_STRIPE: Record<JobStatus, string> = {
  pending: 'border-l-status-pending',
  scheduled: 'border-l-status-scheduled',
  active: 'border-l-status-active',
  on_hold: 'border-l-status-on-hold',
  completed: 'border-l-status-completed',
  cancelled: 'border-l-status-cancelled',
};

export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  pending: 'Pending',
  scheduled: 'Scheduled',
  active: 'Active',
  on_hold: 'On Hold',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const KNOWN: ReadonlySet<string> = new Set(Object.keys(JOB_STATUS_LABELS));

export function isJobStatus(status: unknown): status is JobStatus {
  return typeof status === 'string' && KNOWN.has(status);
}

/**
 * Label for a status that arrived from the API, where the type is a promise
 * rather than a guarantee.
 *
 * THREE OUTCOMES, DELIBERATELY DISTINCT:
 *
 *   known enum value  → its label            ('on_hold' → 'On Hold')
 *   absent            → '—'                  (null, undefined, '')
 *   unknown non-empty → the raw value        ('archived' → 'archived')
 *
 * Absent renders as an em dash and never as a plausible default. Falling back
 * to 'Pending' would be the silent-blank-field bug in its purest form: a job
 * whose status failed to load would read as a real, actionable state.
 *
 * The unknown arm echoes rather than hides on purpose, and it is not the
 * "fallback branch that echoes the raw value" CLAUDE.md warns about — that
 * warning is about using an echo INSTEAD of a map for values you already know.
 * Here the map is exhaustive over the enum, so reaching this arm means the API
 * grew a status the frontend has never heard of. Showing `archived` is how
 * somebody notices; showing '—' or 'Pending' is how it goes unreported for a
 * month.
 */
export function jobStatusLabel(status: unknown): string {
  if (isJobStatus(status)) return JOB_STATUS_LABELS[status];
  if (typeof status === 'string' && status.trim() !== '') return status;
  return '—';
}

/**
 * Badge colour classes for a status off the wire.
 *
 * Unknown and absent both land on neutral grey rather than borrowing a colour
 * that means something — a status nobody has styled must not arrive wearing
 * "active" green.
 */
export function jobStatusStyle(status: unknown): string {
  if (isJobStatus(status)) return JOB_STATUS_STYLES[status];
  return 'bg-fieldiq-dark text-fieldiq-text';
}

/**
 * Tinted background only, for a surface that supplies its own text colour.
 *
 * Unknown and absent land on the same neutral background `jobStatusStyle` uses,
 * for the same reason: a status nobody has styled must not arrive wearing
 * "active" green.
 */
export function jobStatusTint(status: unknown): string {
  if (isJobStatus(status)) return JOB_STATUS_TINT[status];
  return 'bg-fieldiq-dark';
}

/**
 * Solid fill for a status, on a surface that supplies its own text. Pills want
 * `jobStatusStyle`, which pairs this with `text-status-fg`.
 *
 * IT HAS NO PRODUCTION CALL SITE AS OF THE JOBS RE-SKIN, and is kept rather
 * than deleted. Its readers were the dots: two on screens/JobsScreen.tsx and
 * one on screens/DispatchScreen.tsx's job card. The dispatch dot went when the
 * pill beside it went solid, and the jobs pair went when that screen's rows
 * adopted the register pill. What remains is a real distinction — a fill for a
 * surface that brings its own ink, as opposed to `jobStatusStyle`'s fill-plus-
 * ink pair — and the next two screens in this series are the ones most likely
 * to want it. Delete it if they do not.
 *
 * The neutral here is `bg-fieldiq-border` rather than the `bg-gray-500` the old
 * DispatchScreen switch fell through to, because grey now MEANS something —
 * it is `cancelled`. An unrecognised status must not be indistinguishable from
 * a cancelled job.
 */
export function jobStatusSolid(status: unknown): string {
  if (isJobStatus(status)) return JOB_STATUS_SOLID[status];
  return 'bg-fieldiq-border';
}

/**
 * Left-stripe colour for a status off the wire.
 *
 * The neutral is `border-l-fieldiq-border` for the same reason `jobStatusSolid`
 * uses `bg-fieldiq-border`: grey MEANS `cancelled` in this palette, so an
 * unrecognised status must not arrive wearing it. It must still render SOME
 * stripe, though — the stripe is load-bearing for the card's edge against the
 * column (1.15:1 in light), so returning an empty string would make a card with
 * an unknown status effectively invisible rather than merely uncoloured.
 */
export function jobStatusStripe(status: unknown): string {
  if (isJobStatus(status)) return JOB_STATUS_STRIPE[status];
  return 'border-l-fieldiq-border';
}
